import { motion, AnimatePresence } from 'framer-motion';
import { useStars } from '../context/StarContext';
import StarPoint from './StarPoint';
import StillButton from './ui/StillButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlay } from '../App';
import { getStarRectangle, doRectanglesIntersect, generateStarPositions } from '../utils/starPositions';
import VideoStar from './VideoStar';

// Control variables for StarPoint behavior
const EXPAND_STARS_BY_DEFAULT = true;
const ALLOW_STAR_CLICK_TOGGLE = false;

const MessagesScreen = () => {
    const { 
        loading, 
        publicStars, 
        starPositions,
        setStarPositions, 
        maxVisibleStars, 
        loadNextPage, 
        isChangingPage,
        getCurrentPageStars,
        hasMorePages,
        unplacedStars,
        currentPage,
        setCurrentPage
    } = useStars(); 
    const { toggleOverlay } = useOverlay();
    const mountedRef = useRef(false);
    
    // Add refs for measurement tracking
    const measurementsCompleteRef = useRef(false);
    const starMeasurementsRef = useRef(new Map());
    
    // State to control the fade animation of stars
    const [fadeStars, setFadeStars] = useState(false);
    // Add a unique key to force re-render of star container
    const [animationKey, setAnimationKey] = useState(0);
    // Track if we're in the middle of a transition
    const [isTransitioning, setIsTransitioning] = useState(false);
    // Debug mode toggle
    const [debugMode, setDebugMode] = useState(false);
    // Track if we're in mobile view (below 800px)
    const [isMobileView, setIsMobileView] = useState(false);
    // Current star index for mobile carousel
    const [currentStarIndex, setCurrentStarIndex] = useState(0);
    // Touch tracking for swipe
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const carouselRef = useRef(null);
    // Track if we need to load more stars
    const shouldLoadMore = useRef(false);
    // Track swipe direction for animation
    const swipeDirection = useRef(0);
    // Track star size for responsive sizing
    const [starSize, setStarSize] = useState(100);

    // Check for debug mode in session storage
    useEffect(() => {
        const checkDebugMode = () => {
            const debugEnabled = sessionStorage.getItem('DEBUG') === 'true';
            if (debugEnabled !== debugMode) {
                //console.log('Debug mode set from session storage:', debugEnabled);
                setDebugMode(debugEnabled);
            }
        };
        
        // Check on initial load
        checkDebugMode();
        
        // Set up event listener for storage changes
        const handleStorageChange = (e) => {
            if (e.key === 'DEBUG') {
                checkDebugMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        
        // Also check periodically in case session storage is modified directly
        const interval = setInterval(checkDebugMode, 1000);
        
        // Add helper function to window object for easy toggling from console
        window.toggleDebug = (value) => {
            const newValue = value !== undefined ? Boolean(value) : !debugMode;
            sessionStorage.setItem('DEBUG', newValue.toString());
            //console.log(`Debug mode ${newValue ? 'enabled' : 'disabled'}. Refresh the page if needed.`);
        };
        
        // Log instructions for enabling debug mode
        //console.log('To toggle debug mode, run: window.toggleDebug() or sessionStorage.setItem("DEBUG", "true")');
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
            // Clean up the global helper
            delete window.toggleDebug;
        };
    }, [debugMode]);
    
    // Calculate the left offset to shift the star container
    // This helps utilize the leftmost area of the screen
    const [leftOffset, setLeftOffset] = useState(-50); // Default 50px shift to the left
    
    // Adjust the offset based on screen size and set mobile view
    useEffect(() => {
        const handleResize = () => {
            // Check if we're in mobile view
            const mobile = window.innerWidth < 800;
            setIsMobileView(mobile);
            
            // For smaller screens, use a smaller offset
            if (window.innerWidth < 768) {
                setLeftOffset(-30);
            } else {
                setLeftOffset(-50);
            }
            
            // Update star size based on screen width - using MASSIVE values
            if (window.innerWidth >= 640) { // sm breakpoint
                setStarSize(300); 
            } else if (window.innerWidth >= 480) {
                setStarSize(300); 
            } else {
                setStarSize(110); 
            }
            
            // Toggle overlay based on view - force it off in mobile
            if (mobile) {
                toggleOverlay(false);
                // Try to force it with a timeout to override any other code
                setTimeout(() => toggleOverlay(false), 0);
            }
        };
        
        // Set initial value
        handleResize();
        
        // Update on resize
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [toggleOverlay]);

    // Additional effect to ensure overlay stays hidden in mobile view
    useEffect(() => {
        if (isMobileView || !isMobileView) {
            // Force hide overlay in mobile view for MessagesScreen
            toggleOverlay(false);
            
            // Set up interval to ensure overlay stays hidden
            const interval = setInterval(() => toggleOverlay(false), 50);
            
            // Also set up a timeout to hide it after a delay
            const timeout = setTimeout(() => toggleOverlay(false), 100);
            
            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        }
    }, [isMobileView, toggleOverlay, currentStarIndex]); // Also run when currentStarIndex changes

    // Add effect to handle star index bounds
    useEffect(() => {
        if (isMobileView) {
            // For mobile, ensure we have a valid index
            const maxIndex = publicStars.length - 1;
            // Only adjust if we're actually outside the bounds
            if (currentStarIndex < 0 && maxIndex >= 0) {
                //console.log('Adjusting star index from', currentStarIndex, 'to 0');
                setCurrentStarIndex(0);
            } else if (currentStarIndex > maxIndex && maxIndex >= 0) {
                //console.log('Adjusting star index from', currentStarIndex, 'to', maxIndex);
                setCurrentStarIndex(maxIndex);
            }
        } else {
            // For desktop, use the pagination logic
            const stars = getCurrentPageStars();
            if (stars && stars.length > 0) {
                const maxIndex = stars.length - 1;
                if (currentStarIndex > maxIndex) {
                    //console.log('Adjusting star index from', currentStarIndex, 'to', maxIndex);
                    setCurrentStarIndex(maxIndex);
                }
            }
        }
    }, [getCurrentPageStars, currentStarIndex, isMobileView, publicStars.length]);

    // Add function to handle star measurements
    const handleStarMeasurement = useCallback((starId, measurement) => {
        starMeasurementsRef.current.set(starId, measurement);
        
        // Check if all stars have been measured
        const currentPageStars = getCurrentPageStars();
        const allMeasured = currentPageStars.every(star => 
            starMeasurementsRef.current.has(star.usid)
        );
        
        if (allMeasured && !measurementsCompleteRef.current) {
            //console.log('All stars measured, calculating positions');
            measurementsCompleteRef.current = true;
            
            // Calculate positions immediately when all measurements are available
            if (!isMobileView) {
                const { positions, unplacedIndices } = generateStarPositions(
                    currentPageStars.length,
                    currentPageStars,
                    maxVisibleStars,
                    []
                );

                const validPositions = positions.map((pos, index) => {
                    if (!unplacedIndices.includes(index)) {
                        return {
                            ...pos,
                            star: currentPageStars[index]
                        };
                    }
                    return null;
                }).filter(Boolean);

                setStarPositions(validPositions);
                //console.log(`Attempted to place ${currentPageStars.length} stars, successfully placed ${validPositions.length}`);
            }
        }
    }, [getCurrentPageStars, maxVisibleStars, isMobileView]);

    // Update star positions when current page changes and measurements are complete
    useEffect(() => {
        if (!loading && publicStars.length > 0 && !isTransitioning && measurementsCompleteRef.current) {
            if (isMobileView) {
                return;
            }
            
            const currentPageStars = getCurrentPageStars();
            if (currentPageStars.length > 0 && starPositions.length === 0) { // Only calculate if no positions exist
                const { positions, unplacedIndices } = generateStarPositions(
                    currentPageStars.length,
                    currentPageStars,
                    maxVisibleStars,
                    []
                );

                const validPositions = positions.map((pos, index) => {
                    if (!unplacedIndices.includes(index)) {
                        return {
                            ...pos,
                            star: currentPageStars[index]
                        };
                    }
                    return null;
                }).filter(Boolean);

                setStarPositions(validPositions);
                //console.log(`Attempted to place ${currentPageStars.length} stars, successfully placed ${validPositions.length}`);
            }
        }
    }, [loading, publicStars.length, getCurrentPageStars, maxVisibleStars, isTransitioning, isMobileView, measurementsCompleteRef.current]);

    // Reset measurements when page changes
    useEffect(() => {
        if (currentPage !== undefined) {
            measurementsCompleteRef.current = false;
            starMeasurementsRef.current.clear();
            // Clear positions when page changes to prevent showing old positions
            setStarPositions([]);
        }
    }, [currentPage]);

    // Handle page transitions when isChangingPage changes
    useEffect(() => {
        if (!isMobileView && isChangingPage) {
            // Start transition sequence
            setIsTransitioning(true);
            setFadeStars(true);
            
            // After fade out, change animation key and start fade in
            const fadeOutTimer = setTimeout(() => {
                setAnimationKey(prev => prev + 1);
                setFadeStars(false);
                
                // After fade in completes, end transition
                const fadeInTimer = setTimeout(() => {
                    setIsTransitioning(false);
                }, 800);
                
                return () => clearTimeout(fadeInTimer);
            }, 800);
            
            return () => clearTimeout(fadeOutTimer);
        }
    }, [isChangingPage, isMobileView]);

    // Handle swipe in mobile view
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        const swipeThreshold = 50; // Minimum distance to register as a swipe
        const swipeDistance = touchStartX.current - touchEndX.current;
        
        //console.log('Swipe distance:', swipeDistance, 'Current index:', currentStarIndex, 'Total stars:', publicStars.length);
        
        // Check if it's a valid swipe
        if (Math.abs(swipeDistance) > swipeThreshold) {
            if (swipeDistance > 0) {
                // Swipe left - go to next star (direction = 1 for animation)
                if (currentStarIndex < publicStars.length - 1) {
                    //console.log('Swiping left to next star');
                    swipeDirection.current = 1; // Set direction for animation
                    setCurrentStarIndex(prev => Math.min(prev + 1, publicStars.length - 1));
                }
            } else {
                // Swipe right - go to previous star (direction = -1 for animation)
                if (currentStarIndex > 0) {
                    //console.log('Swiping right to previous star');
                    swipeDirection.current = -1; // Set direction for animation
                    setCurrentStarIndex(prev => Math.max(prev - 1, 0));
                }
            }
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        },
        exit: {
            opacity: 1,
            transition: {
                duration: 1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        },
    };
    
    // Star fade animation variants
    const starContainerVariants = {
        visible: { 
            opacity: 1,
            scale: 1,
            transition: { 
                duration: 0.8,
                ease: "easeInOut",
                staggerChildren: 0.1
            }
        },
        hidden: { 
            opacity: 0,
            scale: 0.95,
            transition: { 
                duration: 0.8,
                ease: "easeInOut"
            }
        }
    };

    // Individual star animation variants
    const starVariants = {
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        },
        hidden: {
            opacity: 0,
            scale: 0.8,
            transition: {
                duration: 0.5,
                ease: "easeIn"
            }
        }
    };

    // Mobile carousel variants
    const carouselVariants = {
        enter: (direction) => ({
            x: direction > 0 ? '100%' : '-100%',
            opacity: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: {
                duration: 0.3,
                ease: "easeIn"
            }
        }),
        center: {
            x: 0,
            opacity: 1,
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        },
        exit: (direction) => ({
            x: direction < 0 ? '100%' : '-100%',
            opacity: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: {
                duration: 0.3,
                ease: "easeIn"
            }
        })
    };

    // Handle the "Load More Messages" button click
    const handleLoadMore = useCallback(() => {
        if (!loading && !isTransitioning && !isChangingPage) {
            //console.log('Loading next page of stars...');
            loadNextPage();
        }
    }, [loading, isTransitioning, isChangingPage, loadNextPage]);

    const renderStars = useCallback(() => {
        const currentPageStars = getCurrentPageStars();
        if (!currentPageStars || currentPageStars.length === 0) return null;
        
        // Render all stars, even if they don't have positions yet
        return currentPageStars.map((star, index) => {
            const positionData = starPositions.find(pos => pos.star?.usid === star.usid);
            
            return (
                <StarPoint 
                    key={`${star.usid}-${index}-${animationKey}`}
                    star={star}
                    index={index}
                    position={positionData}
                    onMeasurement={(measurement) => handleStarMeasurement(star.usid, measurement)}
                    initialExpandedState={EXPAND_STARS_BY_DEFAULT}
                    isTextClickable={ALLOW_STAR_CLICK_TOGGLE}
                />
            );
        });
    }, [getCurrentPageStars, starPositions, animationKey, handleStarMeasurement]);

    // Mobile carousel render
    const renderMobileCarousel = useCallback(() => {
        if (publicStars.length === 0) {
            return (
                <div className="w-full h-full flex items-center justify-center">
                    <p className="text-white text-xl">No stars to display</p>
                </div>
            );
        }
        
        // Ensure currentStarIndex is valid and non-negative
        const safeIndex = Math.max(0, Math.min(currentStarIndex, publicStars.length - 1));
        
        // Get the current star
        const star = publicStars[safeIndex];
        
        if (!star) {
            //console.log('No star found at index:', safeIndex, 'Total stars:', publicStars.length);
            return (
                <div className="w-full h-full flex items-center justify-center">
                    <p className="text-white text-xl">Loading star...</p>
                </div>
            );
        }
        
        // Calculate weeks loved for the current star
        const birthDate = new Date(star.dob);
        const today = new Date();
        const diffTime = Math.abs(today - birthDate);
        const weeksLoved = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
        
        // Determine if we have previous/next stars
        const hasPrevious = safeIndex > 0;
        const hasNext = safeIndex < publicStars.length - 1;
        
        //console.log('Carousel state:', {
        //    currentIndex: safeIndex,
        //    totalStars: publicStars.length,
        //    hasPrevious,
        //    hasNext,
        //    starId: star.usid
        //});
        
        return (
            <div 
                ref={carouselRef}
                className="w-full h-full relative"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onClick={() => toggleOverlay(false)}
            >
                {/* Swipe indicators */}
                <div className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between px-1 pointer-events-none z-10">
                    {hasPrevious && (
                        <div className="w-8 h-8 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-still-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </div>
                    )}
                    {hasNext && (
                        <div className="w-8 h-8 flex items-center justify-center ml-auto">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    )}
                </div>
                
                <div className="w-full h-full flex items-center justify-center overflow-hidden">
                    <AnimatePresence initial={false} custom={swipeDirection.current} mode="popLayout">
                        <motion.div
                            key={`mobile-star-${star.usid}-${safeIndex}-${animationKey}`}
                            className="px-6"
                            custom={swipeDirection.current}
                            variants={carouselVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            onAnimationStart={() => toggleOverlay(false)}
                            onAnimationComplete={() => toggleOverlay(false)}
                        >
                            {/* Star content - vertically stacked and centered */}
                            <div className="flex flex-col items-center text-center max-w-md">
                                {/* Name */}
                                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-still-white text-glow-still mb-2">
                                    {star.name.toUpperCase()}
                                </h2>
                                
                                {/* Weeks loved */}
                                <p className="text-lg text-still-white mb-2">
                                    {weeksLoved.toLocaleString()} {weeksLoved === 1 ? 'WEEK' : 'WEEKS'} STILL LOVED
                                </p>
                                
                                {/* Full message */}
                                <p className="text-base text-white mb-0 leading-relaxed px-4">
                                    {star.message}
                                </p>
                                
                                {/* Star video */}
                                <div className="flex items-center justify-center mt-0 p-2">
                                    <div className="relative" style={{ width: `${starSize}px`, height: `${starSize}px` }}>
                                        <VideoStar 
                                            key={`video-star-${star.usid}-${safeIndex}`}
                                            starType={star.starType} 
                                            size={starSize}
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        );
    }, [publicStars, currentStarIndex, animationKey, starSize, toggleOverlay]);

    // Render debug visualization of star rectangles
    const renderDebugRectangles = useCallback(() => {
        if (!debugMode) return null;
        
        const currentPageStars = getCurrentPageStars();
        
        // Create rectangles for each star
        const debugRects = currentPageStars.slice(0, starPositions.length).map((star, index) => {
            if (index < starPositions.length) {
                const position = starPositions[index];
                const rect = getStarRectangle(position, position.scale, star.message);
                
                // Check for overlaps with other stars
                const hasOverlap = starPositions.some((otherPos, otherIndex) => {
                    if (index === otherIndex) return false; // Skip self
                    
                    const otherStar = currentPageStars[otherIndex];
                    if (!otherStar) return false;
                    
                    const otherRect = getStarRectangle(otherPos, otherPos.scale, otherStar.message);
                    return doRectanglesIntersect(rect, otherRect, 5);
                });
                
                // Check if star is too close to the top edge
                const isTooHigh = rect.top < 20;
                
                // Different colors for different states
                let borderColor = 'rgba(0,255,0,0.5)'; // Default: green for no issues
                let fillColor = 'rgba(0,255,0,0.05)';
                
                if (hasOverlap && isTooHigh) {
                    borderColor = 'rgba(255,0,255,0.7)';
                    fillColor = 'rgba(255,0,255,0.1)';
                } else if (hasOverlap) {
                    borderColor = 'rgba(255,0,0,0.7)';
                    fillColor = 'rgba(255,0,0,0.1)';
                } else if (isTooHigh) {
                    borderColor = 'rgba(255,255,0,0.7)';
                    fillColor = 'rgba(255,255,0,0.1)';
                }
                
                return (
                    <div 
                        key={`debug-rect-${index}-${animationKey}`}
                        style={{
                            position: 'absolute',
                            left: rect.left,
                            top: rect.top,
                            width: rect.width,
                            height: rect.height,
                            border: `2px dashed ${borderColor}`,
                            backgroundColor: fillColor,
                            pointerEvents: 'none',
                            zIndex: 1000,
                            borderRadius: '4px',
                            boxSizing: 'border-box'
                        }}
                    >
                        <div style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            fontSize: '10px', 
                            color: 'white',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            padding: '2px 4px',
                            borderRadius: '2px'
                        }}>
                            {star.name} ({index}) - {star.message?.length || 0} chars - {position.scale.toFixed(2)}x
                        </div>
                        
                        {/* Add markers for actual content boundaries */}
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            top: rect.height - 5,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid black',
                            transform: 'translateX(-50%)',
                            zIndex: 1001
                        }} />
                        
                        {/* Add a marker for the text content area */}
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            border: '1px dotted rgba(255, 255, 255, 0.5)',
                            backgroundColor: 'transparent',
                            pointerEvents: 'none',
                            zIndex: 1001,
                            boxSizing: 'border-box'
                        }} />
                    </div>
                );
            }
            return null;
        });
        
        return debugRects;
    }, [debugMode, getCurrentPageStars, starPositions, animationKey]);

    return (
        <div className="fixed inset-0 flex flex-col bg-black bg-opacity-40">
            <motion.div 
                className="relative h-screen overflow-hidden"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                onAnimationStart={() => isMobileView ? toggleOverlay(false) : toggleOverlay(true)}
            >
                {/* Debug mode indicator - only visible when debug is on */}
                {debugMode && (
                    <div className="absolute bottom-4 left-4 z-[9999]">
                        <div className="px-3 py-1 rounded-full text-xs bg-red-600 text-white opacity-50">
                            Debug Mode
                        </div>
                    </div>
                )}
                
                {/* Mobile View */}
                {isMobileView ? (
                    <div className="w-full h-full" onClick={() => toggleOverlay(false)}>
                        {renderMobileCarousel()}
                    </div>
                ) : (
                    /* Desktop View - Star container with left offset to utilize the leftmost area */
                    <AnimatePresence mode="wait">
                        <motion.div 
                            key={`stars-${animationKey}`}
                            className="absolute inset-0 z-10"
                            style={{ 
                                transform: `translateX(${leftOffset}px)`,
                                width: `calc(100% + ${Math.abs(leftOffset)}px)` // Increase width to compensate for the shift
                            }}
                            variants={starContainerVariants}
                            initial="hidden"
                            animate={fadeStars ? "hidden" : "visible"}
                            exit="hidden"
                            onAnimationComplete={() => {
                                if (fadeStars) {
                                    // console.log("Fade out complete");
                                } else {
                                    //console.log("Fade in complete");
                                    if (isTransitioning) {
                                        setIsTransitioning(false);
                                    }
                                }
                            }}
                        >
                            {renderStars()}
                            {renderDebugRectangles()}
                        </motion.div>
                    </AnimatePresence>
                )}
                
                {/* Navigation buttons - Only show Load More on desktop */}
                <motion.div 
                    className="absolute bottom-0 left-0 right-0 flex justify-center space-x-4 z-20 mb-20 sm:mb-20 md:mb-24 lg:mb-28"
                    variants={itemVariants}
                    onAnimationStart={() => isMobileView ? toggleOverlay(false) : null}
                >
                    {!isMobileView && (
                        <StillButton
                            as="button"
                            aria-label="LOAD MORE MESSAGES"
                            onClick={handleLoadMore}
                            disabled={loading || isTransitioning || isChangingPage}
                            className={`${!hasMorePages() ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {(loading || isTransitioning || isChangingPage) ? 'LOADING...' : (hasMorePages() ? 'LOAD MORE MESSAGES' : 'NO MORE MESSAGES')}
                        </StillButton>
                    )}
                    <StillButton
                        to="/space"
                        aria-label="Close"
                        onClick={() => toggleOverlay(false)}
                    >
                        CLOSE
                    </StillButton>
                </motion.div>
            </motion.div>
        </div>
    );
};

export default MessagesScreen; 